import PropTypes from 'prop-types';
import axios from 'axios';
import { createContext, useState, useEffect } from 'react';
import { endpoints } from 'utils/global-constants';
import { getArrayOfSelectedFilterIds } from 'utils';

export const SearchContext = createContext({});

export const SearchFormAndResultsProvider = ({ children }) => {
  const [eventsData, setEventsData] = useState();
  const [articlesData, setArticlesData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);
  const [isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [searchFilters, setSearchFilters] = useState();
  const [sortByFilter, setSortByFilter] = useState();
  const [searchString, setSearchString] = useState();
  const [eventsPaginationNum, setEventsPaginationNum] = useState(1);
  const [articlesPaginationNum, setArticlesPaginationNum] = useState(1);
  let searchPayload;

  const apiEventsUrl = endpoints.performEventSearch;
  const apiArticlesUrl = endpoints.performContentPageSearch;

  const submitEventsSearch = () => {
    axios.post(apiEventsUrl, searchPayload).then((response) => {
      const eventsResponseData = response?.data;
      setEventsData(eventsResponseData);
      setIsLoadingEvents(false);
    });
  };

  const loadMoreEvents = () => {
    axios.post(apiEventsUrl, searchPayload).then((response) => {
      const eventsResponseData = response?.data;

      if (eventsResponseData.events.length) {
        setEventsData((prevState) => ({
          ...eventsResponseData,
          events: [...(prevState ? prevState.events : []), ...eventsResponseData.events],
        }));
      }

      setIsLoadingEvents(false);
    });
  };

  const submitArticlesSearch = () => {
    axios.post(apiArticlesUrl, searchPayload).then((response) => {
      const articlesResponseData = response?.data;
      setArticlesData(articlesResponseData);
      setIsLoadingArticles(false);
    });
  };

  const loadMoreArticles = () => {
    axios.post(apiArticlesUrl, searchPayload).then((response) => {
      const articlesResponseData = response?.data;

      if (articlesResponseData.contentPages.length) {
        setArticlesData((prevState) => ({
          ...articlesResponseData,
          contentPages: [
            ...(prevState ? prevState.contentPages : []),
            ...articlesResponseData.contentPages,
          ],
        }));
      }

      setIsLoadingArticles(false);
    });
  };

  useEffect(() => {
    const arrayOfSearchFilterIds = searchFilters?.length
      ? {
          FilterId: 'searchFilter',
          SelectedOptions: getArrayOfSelectedFilterIds(searchFilters),
        }
      : [];

    searchPayload = {
      SearchString: searchString,
      StartDate: null,
      EndDate: null,
      TileType: 1,
      PageNumEvent: eventsPaginationNum,
      PageNumContent: articlesPaginationNum,
      SortOrder: sortByFilter,
      ItemsPerPage: 6,
      GenreId: '',
      Filters: arrayOfSearchFilterIds,
      PageType: 7,
    };
  }, [searchString, searchFilters, eventsPaginationNum, articlesPaginationNum, sortByFilter]);

  useEffect(() => {
    const searchFiltersArray = getArrayOfSelectedFilterIds(searchFilters);

    if (searchFiltersArray?.length > 1) {
      if (!isLoadingEvents && !isLoadingArticles) {
        setIsLoading(false);
      }
    } else {
      if (searchFiltersArray?.includes('events')) {
        if (!isLoadingEvents) {
          setIsLoading(false);
        }
      }

      if (searchFiltersArray?.includes('contentPages')) {
        if (!isLoadingArticles) {
          setIsLoading(false);
        }
      }
    }
  }, [isLoadingEvents, isLoadingArticles, searchFilters]);

  useEffect(() => {
    const searchFiltersArray = getArrayOfSelectedFilterIds(searchFilters);
    const retainSearchPagination = () => {
      searchPayload.PageNumEvent = 1;
    };

    if (searchFiltersArray?.includes('events')) {
      retainSearchPagination();
      setIsLoadingEvents(true);
      setEventsPaginationNum(1);
      submitEventsSearch();
      setIsLoading(true);
    } else {
      setEventsData();
    }

    if (searchFiltersArray?.includes('contentPages')) {
      retainSearchPagination();
      setIsLoadingArticles(true);
      setArticlesPaginationNum(1);
      submitArticlesSearch();
      setIsLoading(true);
    } else {
      setArticlesData();
    }
  }, [searchFilters, sortByFilter, searchString]);

  useEffect(() => {
    if (eventsPaginationNum !== 1) {
      loadMoreEvents();
    }
  }, [eventsPaginationNum]);

  useEffect(() => {
    if (articlesPaginationNum !== 1) {
      loadMoreArticles();
    }
  }, [articlesPaginationNum]);

  const disableAllFiltersHandler = () => {
    const allSearchFilters = searchFilters.map((filter) => ({
      ...filter,
      selected: false,
    }));
    setSearchFilters(allSearchFilters);
    setEventsData();
    setArticlesData();
    setEventsPaginationNum(1);
    setArticlesPaginationNum(1);
  };

  const updateBrowserHistoryAndUrl = (searchInputValue) => {
    const url = `/search?q=${searchInputValue}`;
    window.history.replaceState(window.history.state, document.title, url);
  };

  const removeSingleFilterHandler = (filterId) => {
    const updateSearchFilters = searchFilters.map((filter) => ({
      ...filter,
      selected: filterId === filter.id ? false : filter.selected,
    }));
    setSearchFilters(updateSearchFilters);

    if (filterId === 'events') {
      setEventsPaginationNum(1);
    }

    if (filterId === 'contentPages') {
      setArticlesPaginationNum(1);
    }
  };

  const modalApplyFiltersButtonHandler = (selectedFilters) => {
    const updatedSearchFilters = searchFilters?.map((filter) => ({
      ...filter,
      selected: selectedFilters?.includes(filter.id),
    }));
    setSearchFilters(updatedSearchFilters);
  };

  const sortByChangeHandler = (sortByString) => {
    const sortByNumber = sortByString && Number(sortByString);
    setIsLoading(true);
    setSortByFilter(sortByNumber);
  };

  const searchFormUpdateHandler = (searchInputValue) => {
    setArticlesPaginationNum(1);
    setEventsPaginationNum(1);
    setSearchString(searchInputValue);
    updateBrowserHistoryAndUrl(searchInputValue);
  };

  const loadMoreEventsButtonHandler = () => {
    setEventsPaginationNum((prevPaginationNum) => prevPaginationNum + 1);
  };

  const loadMoreArticlesButtonHandler = () => {
    setArticlesPaginationNum((prevPaginationNum) => prevPaginationNum + 1);
  };

  const value = {
    eventsData,
    articlesData,
    isLoading,
    searchFilters,
    searchString,
    setSortByFilter,
    setSearchFilters,
    setSearchString,
    disableAllFiltersHandler,
    removeSingleFilterHandler,
    sortByChangeHandler,
    searchFormUpdateHandler,
    loadMoreEventsButtonHandler,
    modalApplyFiltersButtonHandler,
    loadMoreArticlesButtonHandler,
    isLoadingEvents,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

SearchFormAndResultsProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};
